/**
 * useAreaScope — resolves which work-security instruction "sphere" (area) the
 * current screen should work with, and exposes the one value every list/form
 * fetch in the module needs: `areaId`.
 *
 * Resolution order (see PR-2 spec §P1):
 *   1. `?area=<id>` in the URL — the main source, so a link can be forwarded.
 *   2. `localStorage` (`ws.selectedArea.<companyId>`).
 *   3. Exactly one accessible area → `areaId = ""` (see the hard rule below).
 *   4. Otherwise → the system area, else the first by sortOrder.
 * A candidate area (from the URL or storage) that is not among the areas
 * accessible to the user is dropped: `error` is set to "invalid_area" and the
 * module falls back to the step 3/4 default instead.
 *
 * HARD RULE: `areaId` is only ever a non-empty string when there is more than
 * one accessible area AND a specific one is selected. With a single
 * accessible area (today's world for every company except the one piloting
 * "Kibernetinis saugumas"), `areaId` is always `""` and list requests must
 * omit the `areaId` param entirely — byte-for-byte the same request as before
 * areas existed.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchWorkSecurityAreas, WorkSecurityArea } from "services/work-security-areas.service";

/** Consumers (nav badges, other open tabs of the same module) can listen for this. */
export const AREA_CHANGED_EVENT = "ws:area-changed";

const AREA_QUERY_PARAM = "area";

function areaStorageKey(companyId: string): string {
  return `ws.selectedArea.${companyId}`;
}

/** `null` = no preference was ever recorded; `""` is a real choice ("all areas"). */
function readStoredAreaId(companyId: string): string | null {
  try {
    return localStorage.getItem(areaStorageKey(companyId));
  } catch {
    return null; // private mode / storage disabled
  }
}

function writeStoredAreaId(companyId: string, areaId: string): void {
  try {
    localStorage.setItem(areaStorageKey(companyId), areaId);
  } catch {
    // Private browsing / quota exceeded — the in-memory selection still works.
  }
}

function clearStoredAreaId(companyId: string): void {
  try {
    localStorage.removeItem(areaStorageKey(companyId));
  } catch {
    // Nothing to do — see readStoredAreaId.
  }
}

function defaultAreaId(accessible: WorkSecurityArea[]): string {
  const system = accessible.find((area) => area.isSystem);
  return system ? system.id : accessible[0]?.id || "";
}

export type AreaScopeError = "load_failed" | "invalid_area" | null;

export interface UseAreaScopeResult {
  /** Areas accessible to the current user (never includes archived ones). */
  areas: WorkSecurityArea[];
  /** True for Master — gates the "Tvarkyti sritis" settings link. */
  canManageAreas: boolean;
  /** True while the initial /areas request is in flight. */
  loading: boolean;
  error: AreaScopeError;
  /**
   * The id to send to list/create endpoints. Empty string means "don't
   * filter by area" — see the hard rule in the module doc above.
   */
  areaId: string;
  /** The resolved area object for a concrete `areaId`; null in "all areas" mode. */
  selectedArea: WorkSecurityArea | null;
  setAreaId: (id: string) => void;
  /** True once there is more than one accessible area — shows the switcher row. */
  showSwitcher: boolean;
  /**
   * True once it is safe for consumers to fire their list fetches. It is
   * true from the very first render UNLESS the URL carries a `?area=` that
   * still needs to be validated against /areas — that is the one case where
   * blocking matters, so a foreign/stale link doesn't flash another sphere's
   * list before the 403 lands. Consumers should gate their fetch effects on
   * this flag; everywhere else it is already true, so the roles list keeps
   * firing in parallel with /areas exactly like it does today.
   */
  resolved: boolean;
}

export function useAreaScope(companyId: string | undefined): UseAreaScopeResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const [areas, setAreas] = useState<WorkSecurityArea[]>([]);
  const [canManageAreas, setCanManageAreas] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AreaScopeError>(null);
  const [areaId, setAreaIdState] = useState<string>("");

  // A `?area=` in the URL must gate dependent fetches until it is validated;
  // everything else (no url param at all) resolves immediately.
  const [resolved, setResolved] = useState<boolean>(() => !searchParams.has(AREA_QUERY_PARAM));

  useEffect(() => {
    // Reset on company switch so the previous company's areas/selection never
    // leak into the new one while its /areas request is in flight.
    setAreas([]);
    setCanManageAreas(false);
    setLoading(true);
    setError(null);
    setAreaIdState("");
    setResolved(!searchParams.has(AREA_QUERY_PARAM));

    if (!companyId) {
      setLoading(false);
      setResolved(true);
      return undefined;
    }

    let alive = true;
    fetchWorkSecurityAreas(companyId)
      .then((data) => {
        if (!alive) return;
        const accessible = data.areas || [];
        setAreas(accessible);
        setCanManageAreas(Boolean(data.canManageAreas));

        const hasUrlArea = searchParams.has(AREA_QUERY_PARAM);
        const urlCandidate = hasUrlArea ? searchParams.get(AREA_QUERY_PARAM) || "" : null;
        const storedCandidate = readStoredAreaId(companyId);
        const candidate = hasUrlArea ? urlCandidate : storedCandidate;
        const candidateSource: "url" | "storage" | null = hasUrlArea
          ? "url"
          : storedCandidate !== null
            ? "storage"
            : null;

        if (accessible.length <= 1) {
          // HARD RULE: never send areaId when there's nothing to disambiguate.
          setAreaIdState("");
        } else if (candidate === null) {
          // No preference recorded anywhere — system area, else first by order.
          setAreaIdState(defaultAreaId(accessible));
        } else if (candidate === "" || accessible.some((area) => area.id === candidate)) {
          // Valid — including a link forwarded with `?area=<id>`. Leave the
          // URL as-is so it stays shareable/bookmarkable.
          setAreaIdState(candidate);
        } else {
          // A foreign/stale reference — drop it, warn, and fall back to the
          // default. Clear whichever source produced it so it doesn't keep
          // re-litigating an already-rejected choice on the next load.
          setError("invalid_area");
          if (candidateSource === "storage") clearStoredAreaId(companyId);
          if (candidateSource === "url") {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.delete(AREA_QUERY_PARAM);
                return next;
              },
              { replace: true }
            );
          }
          setAreaIdState(defaultAreaId(accessible));
        }
      })
      .catch(() => {
        if (!alive) return;
        // Degrade to "all accessible" rather than blocking the module.
        setAreas([]);
        setCanManageAreas(false);
        setError("load_failed");
        setAreaIdState("");
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
        setResolved(true);
      });

    return () => {
      alive = false;
    };
    // Intentionally only re-runs on companyId: the URL/storage candidates are
    // read once, at resolution time — after that, only setAreaId (below)
    // changes the selection, so editing `?area` elsewhere doesn't re-trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const setAreaId = useCallback(
    (id: string) => {
      setAreaIdState(id);
      setError(null);
      if (companyId) writeStoredAreaId(companyId, id);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set(AREA_QUERY_PARAM, id);
          else next.delete(AREA_QUERY_PARAM);
          return next;
        },
        { replace: true }
      );
      window.dispatchEvent(new CustomEvent(AREA_CHANGED_EVENT));
    },
    [companyId, setSearchParams]
  );

  const selectedArea = areaId ? areas.find((area) => area.id === areaId) || null : null;

  return {
    areas,
    canManageAreas,
    loading,
    error,
    areaId,
    selectedArea,
    setAreaId,
    showSwitcher: areas.length > 1,
    resolved,
  };
}
